import { useEffect, useState, useCallback, useRef } from 'react';
import signalRNotificationService from 'services/signalrNotificationService';

/**
 * Custom hook for managing SignalR notifications
 * @param {Object} options - Configuration options
 * @param {string} options.accessToken - JWT token for authentication
 * @param {boolean} options.autoConnect - Auto-connect on mount (default: true)
 * @param {function} options.onNotification - Callback when notification received
 * @param {function} options.onError - Callback when error occurs
 * @returns {Object} - Notification state and control functions
 */
export const useSignalRNotifications = (options = {}) => {
  const {
    accessToken,
    autoConnect = true,
    onNotification,
    onError
  } = options;

  const [connectionState, setConnectionState] = useState('Disconnected');
  const [notifications, setNotifications] = useState([]);
  const [error, setError] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  
  // Use refs to track subscriptions
  const subscriptionsRef = useRef([]);
  const isInitializedRef = useRef(false);

  /**
   * Connect to SignalR hub
   */
  const connect = useCallback(async () => {
    if (!accessToken) {
      console.warn('⚠️ Cannot connect: Missing access token');
      setError({ message: 'Missing access token' });
      return false;
    }

    if (isConnecting) {
      console.warn('⚠️ Connection already in progress');
      return false;
    }

    try {
      setIsConnecting(true);
      setError(null);
      setConnectionState('Connecting');
      
      await signalRNotificationService.startConnection(accessToken);
      
      setConnectionState('Connected');
      setIsConnecting(false);
      return true;
    } catch (err) {
      const errorObj = {
        message: err?.message || 'Failed to connect to notification service',
        error: err
      };
      
      setError(errorObj);
      setConnectionState('Disconnected');
      setIsConnecting(false);
      
      if (onError) {
        onError(errorObj);
      }
      
      return false;
    }
  }, [accessToken, isConnecting, onError]);

  /**
   * Disconnect from SignalR hub
   */
  const disconnect = useCallback(async () => {
    try {
      await signalRNotificationService.stopConnection();
      setConnectionState('Disconnected');
      setNotifications([]);
      setError(null);
    } catch (err) {
      console.error('Error disconnecting:', err);
    }
  }, []);

  /**
   * Clear all notifications
   */
  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  /**
   * Remove a specific notification
   */
  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  /**
   * Manually refresh connection (disconnect and reconnect)
   */
  const refreshConnection = useCallback(async () => {
    await disconnect();
    await connect();
  }, [connect, disconnect]);

  // Subscribe to SignalR events
  useEffect(() => {
    // Prevent multiple subscriptions
    if (subscriptionsRef.current.length > 0) {
      return;
    }

    // Notification handler
    const handleNotification = (data) => {
      const notification = {
        id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        ...data,
        receivedAt: new Date().toISOString(),
        isRead: false
      };

      setNotifications(prev => [notification, ...prev]);
      
      // Call custom callback if provided
      if (onNotification) {
        onNotification(notification);
      }
    };

    // Connection state handlers
    const handleReconnecting = () => {
      // console.log('📡 Reconnecting to SignalR...');
      setConnectionState('Reconnecting');
    };

    const handleReconnected = ({ connectionId }) => {
      // console.log('✅ Reconnected to SignalR:', connectionId);
      setConnectionState('Connected');
      setError(null);
    };

    const handleClosed = ({ error: closeError }) => {
      // console.log('❌ SignalR connection closed');
      setConnectionState('Disconnected');
      
      if (closeError) {
        const errorObj = {
          message: 'Connection closed unexpectedly',
          error: closeError
        };
        setError(errorObj);
        
        if (onError) {
          onError(errorObj);
        }
      }
    };

    const handleError = (errorData) => {
      console.error('❌ SignalR error:', errorData);
      setError(errorData);
      
      if (onError) {
        onError(errorData);
      }
    };

    // Subscribe to all events
    const notificationSub = signalRNotificationService.subscribe('notification', handleNotification);
    const reconnectingSub = signalRNotificationService.subscribe('reconnecting', handleReconnecting);
    const reconnectedSub = signalRNotificationService.subscribe('reconnected', handleReconnected);
    const closedSub = signalRNotificationService.subscribe('closed', handleClosed);
    const errorSub = signalRNotificationService.subscribe('error', handleError);

    // Store subscription IDs for cleanup
    subscriptionsRef.current = [
      notificationSub,
      reconnectingSub,
      reconnectedSub,
      closedSub,
      errorSub
    ];

    // Auto-connect if enabled and not already initialized
    if (autoConnect && !isInitializedRef.current && accessToken) {
      isInitializedRef.current = true;
      connect();
    }

    // Cleanup subscriptions on unmount
    return () => {
      subscriptionsRef.current.forEach(subId => {
        signalRNotificationService.unsubscribe(subId);
      });
      subscriptionsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  // Cleanup on unmount if autoConnect was used
  useEffect(() => {
    return () => {
      if (autoConnect && isInitializedRef.current) {
        disconnect();
        isInitializedRef.current = false;
      }
    };
  }, [autoConnect, disconnect]);

  // Mark notification as read
  const markAsRead = useCallback((id) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, isRead: true } : n)
    );
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev =>
      prev.map(n => ({ ...n, isRead: true }))
    );
  }, []);

  // Get unread count
  const unreadCount = notifications.filter(n => !n.isRead).length;

  return {
    // State
    connectionState,
    notifications,
    unreadCount,
    error,
    isConnecting,
    isConnected: connectionState === 'Connected',
    
    // Actions
    connect,
    disconnect,
    refreshConnection,
    clearNotifications,
    removeNotification,
    markAsRead,
    markAllAsRead,
    
    // Utility
    connectionId: signalRNotificationService.getConnectionId(),
  };
};

export default useSignalRNotifications;

